import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import {
  Bell,
  FileText,
  CreditCard,
  MessageSquare,
  CheckCheck,
  ChevronDown,
  AlertTriangle,
  X,
  UserPlus,
  UserMinus,
  BarChart3,
  FileAudio,
  ShieldOff,
  Trash2,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const DEADLINE_TYPES = new Set(["deadline_warning", "deadline_overdue"]);

const TYPE_CONFIG: Record<string, { icon: typeof Bell; bg: string }> = {
  new_contract: { icon: FileText, bg: "bg-slate-700" },
  invoice_sent: { icon: CreditCard, bg: "bg-slate-500" },
  invoice_paid: { icon: CreditCard, bg: "bg-emerald-600" },
  invoice_overdue: { icon: AlertTriangle, bg: "bg-red-500" },
  invoice_comment: { icon: MessageSquare, bg: "bg-slate-600" },
  chat_mention: { icon: MessageSquare, bg: "bg-slate-800" },
  recruiter_assigned: { icon: UserPlus, bg: "bg-blue-600" },
  recruiter_unassigned: { icon: UserMinus, bg: "bg-orange-500" },
  closing_probability: { icon: BarChart3, bg: "bg-violet-600" },
  // Видеоинтервью. Красный — там, где надо что-то сделать прямо сейчас: отзыв
  // согласия гасит ссылку у заказчика, и ему надо позвонить.
  video_transcript_ready: { icon: FileAudio, bg: "bg-emerald-600" },
  video_transcript_failed: { icon: AlertTriangle, bg: "bg-orange-500" },
  video_consent_withdrawn: { icon: ShieldOff, bg: "bg-red-600" },
  video_deletion_requested: { icon: Trash2, bg: "bg-amber-600" },
  video_deletion_decided: { icon: Trash2, bg: "bg-slate-600" },
};

function timeAgo(date: Date | string): string {
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "сейчас";
  if (diffMin < 60) return `${diffMin} мин`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} ч`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays === 1) return "вчера";
  return `${diffDays} дн`;
}

const INITIAL_READ_COUNT = 5;
const LOAD_MORE_COUNT = 7;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const [readLimit, setReadLimit] = useState(INITIAL_READ_COUNT);

  const { data: unreadCount = 0, refetch: refetchCount } =
    trpc.notifications.getUnreadCount.useQuery(undefined, {
      // Один источник запроса — на mount + интервал. staleTime=20s + refetchOnMount=false
      // убирают дублирующий запрос от Strict Mode / повторного монтирования.
      refetchInterval: 30_000,
      staleTime: 20_000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  const { data: rawNotifications = [], refetch } =
    trpc.notifications.getAll.useQuery(undefined, {
      enabled: open,
    });

  const notifications = rawNotifications.filter(
    (n: any) => !DEADLINE_TYPES.has(n.type)
  );

  const { unread, read, hasMoreRead } = useMemo(() => {
    const unread = notifications.filter((n: any) => !n.isRead);
    const allRead = notifications.filter((n: any) => n.isRead);
    const read = allRead.slice(0, readLimit);
    return { unread, read, hasMoreRead: allRead.length > readLimit };
  }, [notifications, readLimit]);

  const markAsRead = trpc.notifications.markAsRead.useMutation({
    onSuccess: () => {
      refetch();
      refetchCount();
    },
  });

  const markAllAsRead = trpc.notifications.markAllAsRead.useMutation({
    onSuccess: () => {
      refetch();
      refetchCount();
    },
  });

  const handleClick = (notif: (typeof notifications)[0]) => {
    if (!notif.isRead) {
      markAsRead.mutate({ id: notif.id });
    }
    const vacancyId = (() => {
      if (notif.link) {
        const url = new URL(notif.link, window.location.origin);
        const v = url.searchParams.get("vacancy");
        if (v) return Number(v);
      }
      return (notif as any).relatedVacancyId ?? null;
    })();

    if (notif.link) {
      const url = new URL(notif.link, window.location.origin);
      // Add vacancy param if missing but we have relatedVacancyId
      if (
        !url.searchParams.get("vacancy") &&
        vacancyId &&
        url.pathname === "/kanban"
      ) {
        url.searchParams.set("vacancy", String(vacancyId));
      }
      navigate(url.pathname + url.search);
    } else if (vacancyId) {
      navigate(`/kanban?vacancy=${vacancyId}`);
    }

    if (vacancyId) {
      window.dispatchEvent(
        new CustomEvent("open-vacancy", {
          detail: {
            vacancyId,
            tab: notif.type === "chat_mention" ? "comments" : undefined,
          },
        })
      );
    }
    setOpen(false);
  };

  const renderNotif = (notif: (typeof notifications)[0]) => {
    const config = TYPE_CONFIG[notif.type] ?? {
      icon: Bell,
      bg: "bg-slate-400",
    };
    const Icon = config.icon;
    return (
      <button
        key={notif.id}
        onClick={() => handleClick(notif)}
        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
          !notif.isRead ? "bg-white hover:bg-slate-50" : "hover:bg-slate-50/60"
        }`}
      >
        <div
          className={`mt-0.5 shrink-0 w-8 h-8 rounded-[10px] ${config.bg} flex items-center justify-center shadow-sm`}
        >
          <Icon className="h-4 w-4 text-white" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <p
              className={`text-[13px] leading-snug ${!notif.isRead ? "font-semibold text-slate-900" : "font-medium text-slate-500"}`}
            >
              {notif.title}
            </p>
            <span
              className={`text-[11px] shrink-0 tabular-nums ${!notif.isRead ? "text-slate-800" : "text-slate-300"}`}
            >
              {timeAgo(notif.createdAt)}
            </span>
          </div>
          <p
            className={`text-[12px] leading-relaxed mt-0.5 whitespace-pre-line ${!notif.isRead ? "text-slate-500" : "text-slate-400"}`}
          >
            {notif.message}
          </p>
        </div>
        {!notif.isRead && (
          <div className="mt-3 shrink-0 w-[9px] h-[9px] rounded-full bg-slate-800" />
        )}
      </button>
    );
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) setReadLimit(INITIAL_READ_COUNT);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8 rounded-lg hover:bg-neutral-100"
        >
          <Bell className="size-4 text-neutral-500" />
          {unreadCount > 0 && (
            <span className="absolute right-1 top-1 flex items-center justify-center rounded-full bg-[oklch(0.556_0_0)] text-[9px] font-semibold text-white min-w-[14px] h-3.5 px-0.5 border border-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(400px,calc(100vw-24px))] p-0 rounded-2xl shadow-[0_8px_30px_-4px_rgba(0,0,0,0.15)] border-0 overflow-hidden bg-white/95 backdrop-blur-xl"
        align="end"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100/80">
          <h3 className="font-semibold text-[15px] text-slate-900 tracking-tight">
            Уведомления
          </h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead.mutate()}
                className="text-[13px] font-medium text-slate-800 hover:text-slate-900 transition-colors"
              >
                Прочитать все
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-slate-100 transition-colors"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {unread.length === 0 && read.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Bell className="w-5 h-5 text-slate-300" />
              </div>
              <p className="text-[13px] text-slate-400">Нет уведомлений</p>
            </div>
          ) : (
            <div>
              {unread.length > 0 && (
                <div className="divide-y divide-slate-100/60">
                  {unread.map(renderNotif)}
                </div>
              )}

              {read.length > 0 && (
                <div>
                  {unread.length > 0 && (
                    <div className="px-4 py-1.5 border-t border-slate-100/80">
                      <span className="text-[11px] font-medium text-slate-300 uppercase tracking-wider">
                        Ранее
                      </span>
                    </div>
                  )}
                  <div className="divide-y divide-slate-50">
                    {read.map(renderNotif)}
                  </div>
                  {hasMoreRead && (
                    <button
                      onClick={() =>
                        setReadLimit(prev => prev + LOAD_MORE_COUNT)
                      }
                      className="w-full flex items-center justify-center gap-1 px-4 py-2.5 text-[13px] font-medium text-slate-800 hover:bg-slate-50 transition-colors border-t border-slate-100/60"
                    >
                      Показать ещё
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
